import * as amplitude from '@amplitude/analytics-browser';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { match, Pattern } from 'ts-pattern';

import { useCheckVatNumberInUse, useFetchVatInformation } from '~/api/auth';
import { FetchError, HttpError, HttpTooManyRequestsError } from '~/api/errors';
import { Alert, Button, ErrorMessage, ExternalLink, Separator } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { AmplitudeEventName, AmplitudeEventProperty } from '~/constants/amplitude';
import { env } from '~/constants/env';
import { SearchParamKeys } from '~/constants/url';
import { routes } from '~/providers/RouterProvider/router.routes';
import { checkEnterpriseNumberChecksum, cleanEnterpriseNumber } from '~/utils/string';

import { AuthLayoutContent } from '../AuthLayout/AuthLayoutContent';
import { AuthLayoutTitleGroup } from '../AuthLayout/AuthLayoutTitleGroup';
import { AuthLink } from '../AuthLink/AuthLink';
import { useRegistrationContext } from './Registration.context';
import { RegistrationAddReferralCodeButton } from './RegistrationReferralCode/RegistrationAddReferralCodeButton';
import { RegistrationReferralCode } from './RegistrationReferralCode/RegistrationReferralCode';
import { buttonsStyle, buttonStyle, contentWrapperStyle, formStyle } from './RegistrationVatInformation.css';

export const RegistrationVatInformation = () => {
  const fetchVatInformationMutation = useFetchVatInformation();
  const checkVatNumberInUseMutation = useCheckVatNumberInUse();

  const { hadReferralCode, setVatInformation, vatInformation } = useRegistrationContext();
  const [searchParams] = useSearchParams();

  const { control, formState, handleSubmit, setError } = useForm<{ vatNumber: string }>({
    defaultValues: {
      vatNumber: vatInformation?.vatNumber.slice(2) || searchParams.get(SearchParamKeys.REGISTRATION_ENTERPRISE_NUMBER) || '',
    },
  });
  const formId = useId();

  const [autoAnimateRef] = useAutoAnimate();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common', 'validation']);

  const onSubmit = handleSubmit((data) => {
    const vatNumber = `BE${cleanEnterpriseNumber(data.vatNumber)}`;

    fetchVatInformationMutation.mutate(vatNumber, {
      onSuccess: (vatInformation) => {
        if (!vatInformation.vatValid) {
          return setError(
            'vatNumber',
            { message: t('auth:registrationPage.stepVatNumber.form.vatNumberField.errors.invalidVatNumber') },
            { shouldFocus: true },
          );
        }

        if (!vatInformation.vatActive) {
          return setError(
            'vatNumber',
            { message: t('auth:registrationPage.stepVatNumber.form.vatNumberField.errors.inactiveVatNumber') },
            { shouldFocus: true },
          );
        }

        setVatInformation({ ...vatInformation, fetchedAt: Date.now() });

        const isCompany = vatInformation.enterpriseType !== 0;

        if (isCompany) {
          return navigate(routes.registerCompany);
        }

        amplitude.track(AmplitudeEventName.RegistrationStep1Completed, {
          [AmplitudeEventProperty.RegistrationEligible]: vatInformation.hasEligibleNacebelCodes,
        });

        navigate(routes.registerAccountInformation);
      },
    });
  });

  const renderAsyncError = (error: unknown) =>
    match(error)
      .with(Pattern.instanceOf(HttpTooManyRequestsError), () => t('common:errors.tooManyRequests'))
      .with(Pattern.instanceOf(HttpError), () => (
        <Trans
          components={{ email: <ExternalLink /> }}
          i18nKey="auth:registrationPage.stepVatNumber.form.vatNumberField.errors.httpError"
        />
      ))
      .with(Pattern.instanceOf(FetchError), () => t('common:errors.fetchError'))
      .otherwise(() => t('auth:registrationPage.stepVatNumber.form.vatNumberField.errors.fallback'));

  return (
    <AuthLayoutContent>
      <AuthLayoutTitleGroup
        eyebrow={t('auth:registrationPage.common.header.pill')}
        subtitle={t('auth:registrationPage.stepVatNumber.header.subtitle')}
        title={t('auth:registrationPage.stepVatNumber.header.title')}
      />

      <div className={contentWrapperStyle} ref={autoAnimateRef}>
        {env.KBO_UNAVAILABLE && (
          <Alert announce icon="Warning" status="warning" title={t('auth:registrationPage.stepVatNumber.alert.kboNotAvailable.title')}>
            <Trans components={{ break: <br /> }} i18nKey="auth:registrationPage.stepVatNumber.alert.kboNotAvailable.description" />
          </Alert>
        )}

        <Form className={formStyle} id={formId} onSubmit={onSubmit}>
          <Controller
            control={control}
            name="vatNumber"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label
                  infotip={
                    <Trans
                      components={{ faqLink: <ExternalLink /> }}
                      i18nKey="auth:registrationPage.stepVatNumber.form.vatNumberField.description"
                    />
                  }
                >
                  {t('auth:registrationPage.stepVatNumber.form.vatNumberField.label')}
                </Field.Label>

                <Input
                  autoFocus
                  inputMode="decimal"
                  onBlur={onBlur}
                  onValueChange={(value) => {
                    onChange(value);
                    if (fetchVatInformationMutation.isError) fetchVatInformationMutation.reset();
                    if (checkVatNumberInUseMutation.isError) checkVatNumberInUseMutation.reset();
                  }}
                  prefix="BE"
                  ref={ref}
                  transformValue={(value) => (value.trimStart().toUpperCase().startsWith('BE') ? value.trimStart().slice(2) : value)}
                  value={value}
                />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
                {checkVatNumberInUseMutation.isError && <ErrorMessage>{renderAsyncError(checkVatNumberInUseMutation.error)}</ErrorMessage>}
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: {
                notEmpty: (value) => !!value.trim() || t('validation:required'),
                vatNumberLength: (value) =>
                  /^(?:0?[1-9]\d{8}|1\d{9})$/.test(value.replace(/\D/g, '')) ||
                  t('auth:registrationPage.stepVatNumber.form.vatNumberField.errors.vatNumberLength'),
                vatNumberChecksum: (value) => {
                  const enterpriseNumber = cleanEnterpriseNumber(value);
                  const isDummyEnterpriseNumber = enterpriseNumber === '0123456789' && !env.IS_PRODUCTION_ENV;

                  return (
                    checkEnterpriseNumberChecksum(enterpriseNumber) ||
                    isDummyEnterpriseNumber ||
                    t('auth:registrationPage.stepVatNumber.form.vatNumberField.errors.vatNumberChecksum')
                  );
                },
                unique: async (value) => {
                  try {
                    const inUse = await checkVatNumberInUseMutation.mutateAsync({ vatNumber: `BE${cleanEnterpriseNumber(value)}` });

                    return !inUse || t('auth:registrationPage.stepVatNumber.form.vatNumberField.errors.exists');
                  } catch {
                    // Block submission. The detailed reason is shown by the `ErrorMessage` above.
                    return false;
                  }
                },
              },
            }}
          />

          {fetchVatInformationMutation.isError && <ErrorMessage>{renderAsyncError(fetchVatInformationMutation.error)}</ErrorMessage>}
        </Form>

        <RegistrationReferralCode />

        <div className={buttonsStyle}>
          <Button
            className={buttonStyle}
            form={formId}
            loading={formState.isValidating || fetchVatInformationMutation.isPending}
            type="submit"
          >
            {t('auth:registrationPage.stepVatNumber.form.submitButton')}
          </Button>

          {hadReferralCode && <RegistrationAddReferralCodeButton className={buttonStyle} />}
        </div>
      </div>

      <Separator text={t('common:or')} />

      <AuthLink to="login" />
    </AuthLayoutContent>
  );
};
